/**
 * Contrato HTTP dos fluxos (Typechat, formulário, quiz).
 *
 * O modelo do grafo — blocos, arestas, execução — vive em `@rn/core/flow`.
 * Aqui fica só o que atravessa a rede: o que a tela manda salvar, o que a
 * página pública pede e o que ela recebe. A validação de verdade do grafo
 * (laço sem pergunta, seta solta, opção sumida) é do `validateFlow` do core;
 * o zod daqui só garante o formato para o servidor não engasgar.
 *
 * Ver docs/TYPECHAT.md.
 */
import { z } from "zod";
export const FLOW_KINDS = ["chat", "form", "quiz"];
export const FLOW_KIND_LABEL = {
    chat: "Typechat",
    form: "Formulário",
    quiz: "Quiz",
};
export const FLOW_STATUSES = ["draft", "published", "archived"];
export const FLOW_STATUS_LABEL = {
    draft: "Rascunho",
    published: "Publicado",
    archived: "Arquivado",
};
export const FLOW_SESSION_STATUS_LABEL = {
    active: "Em andamento",
    waiting_input: "Esperando resposta",
    handoff: "Com a equipe",
    completed: "Concluído",
    abandoned: "Abandonado",
    failed: "Falhou",
};
/** Canais em que um fluxo pode rodar. */
export const FLOW_CHANNELS = ["web", "whatsapp_cloud", "whatsapp_evolution"];
export const flowSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, "Use pelo menos 3 caracteres")
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Só letras minúsculas, números e hífen");
// ── Grafo (formato) ─────────────────────────────────────────────────────────
const conditionSchema = z.object({
    field: z.string().min(1).max(120),
    operator: z.enum([
        "eq",
        "neq",
        "gt",
        "gte",
        "lt",
        "lte",
        "in",
        "not_in",
        "contains",
        "exists",
        "not_exists",
    ]),
    value: z.unknown().optional(),
});
const flowEdgeSchema = z.object({
    to: z.string().min(1).max(64),
    when: z.array(conditionSchema).max(10).optional(),
    option: z.string().max(64).optional(),
    branch: z.string().max(64).optional(),
});
const flowNodeSchema = z
    .object({
    id: z.string().min(1).max(64),
    type: z.string().min(1).max(40),
    data: z.record(z.string(), z.unknown()),
    next: z.array(flowEdgeSchema).max(30).optional(),
    position: z.object({ x: z.number(), y: z.number() }).optional(),
})
    .passthrough();
export const flowGraphSchema = z
    .object({
    version: z.literal(1),
    start: z.string().min(1).max(64),
    nodes: z.record(z.string(), flowNodeSchema),
})
    .refine((g) => Object.keys(g.nodes).length <= 300, "Fluxo grande demais (máximo 300 blocos)");
// ── Tema e ajustes ──────────────────────────────────────────────────────────
const cor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Cor no formato #RRGGBB");
export const FLOW_FONTS = [
    "Manrope",
    "Inter",
    "Sora",
    "Poppins",
    "Playfair Display",
    "DM Sans",
];
export const flowThemeSchema = z
    .object({
    primaryColor: cor.optional(),
    backgroundColor: cor.optional(),
    textColor: cor.optional(),
    fontFamily: z.enum(FLOW_FONTS).optional(),
    logoUrl: z.string().max(2048).optional(),
    coverUrl: z.string().max(2048).optional(),
    radius: z.enum(["none", "md", "full"]).optional(),
    /** Texto do botão de avançar no formulário. */
    buttonLabel: z.string().max(40).optional(),
    /** Mostra a barra de progresso (formulário e quiz). */
    showProgress: z.boolean().optional(),
    /**
     * Foto de fundo em tela cheia, fixa atrás do conteúdo (nos três modos).
     * Sempre com a camada de `backgroundOverlay` por cima: foto crua atrás de
     * texto quase nunca passa no contraste.
     */
    backgroundImageUrl: z.string().max(2048).optional(),
    /** Escurecimento sobre a foto, em % da cor de fundo. Padrão 60 quando há foto. */
    backgroundOverlay: z.number().int().min(0).max(90).optional(),
})
    .strict();
/** Escurecimento padrão sobre a foto de fundo, quando o tema não define. */
export const FLOW_BACKGROUND_OVERLAY_DEFAULT = 60;
/**
 * Tela de abertura (formulário e quiz; no chat não — lá a conversa já é a
 * abertura). É só da página: a sessão continua sendo criada no carregamento,
 * então quem para na abertura conta como visita iniciada, igual a antes.
 * Título e texto aceitam *negrito* e quebra de linha.
 */
export const flowWelcomeSchema = z
    .object({
    enabled: z.boolean(),
    title: z.string().max(200),
    text: z.string().max(600).optional(),
    buttonLabel: z.string().max(40).optional(),
    mediaUrl: z.string().max(2048).optional(),
})
    .strict();
// ── Agente de IA (tela "Agente de IA") ──────────────────────────────────────
//
// Configuração simples (comportamento, roteiro, conhecimento, canais) que o
// `buildAgentGraph` do core transforma num Typechat comum. Fica em
// `flows.settings.agent`, e por isso entra no `flowSettingsSchema`: o editor
// avançado salva os ajustes inteiros, e um campo desconhecido num schema
// `.strict()` faria todo salvamento de um agente aberto lá dar 400.
//
// O schema é tolerante de propósito (só tetos de tamanho, quase nada de
// mínimo): a tela salva sozinha enquanto a pessoa digita, e um campo vazio no
// meio da digitação não pode virar erro. O que precisa estar certo para ir ao
// ar (nome, objetivo, link https, canal, provedor de IA) é conferido no
// checklist da publicação.
export const AGENT_TONES = ["acolhedor", "objetivo", "descontraido", "formal"];
export const AGENT_TONE_LABEL = {
    acolhedor: "Acolhedor",
    objetivo: "Objetivo",
    descontraido: "Descontraído",
    formal: "Formal",
};
export const AGENT_GOALS = ["agendar", "qualificar", "link"];
export const AGENT_GOAL_LABEL = {
    agendar: "Agendar a avaliação",
    qualificar: "Qualificar e passar para a equipe",
    link: "Mandar um link",
};
const agentTextList = (max) => z.array(z.string().trim().max(200)).max(max).default([]);
export const agentStepSchema = z
    .object({
    id: z.string().min(1).max(40),
    ask: z.string().max(300),
    /** Gerado pela tela a partir do texto; o core corrige se vier inválido. */
    variable: z.string().max(60),
    required: z.boolean().default(true),
})
    .strict();
export const agentConfigSchema = z
    .object({
    behavior: z
        .object({
        name: z.string().trim().max(40).default(""),
        presentation: z.string().trim().max(200).default(""),
        tone: z.enum(AGENT_TONES).default("acolhedor"),
        dos: agentTextList(10),
        donts: agentTextList(10),
        welcome: z.string().max(600).optional(),
        hours: z
            .object({
            mode: z.enum(["always", "outside_business_hours"]).default("always"),
            outsideMessage: z.string().max(600).optional(),
        })
            .strict()
            .default({ mode: "always" }),
    })
        .strict(),
    script: z
        .object({
        steps: z.array(agentStepSchema).max(12).default([]),
        goal: z
            .object({
            type: z.enum(AGENT_GOALS).default("qualificar"),
            linkText: z.string().max(300).optional(),
            linkUrl: z.string().trim().max(2000).optional(),
        })
            .strict()
            .default({ type: "qualificar" }),
        handoff: z
            .object({
            keywords: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
            afterMessages: z.number().int().min(2).max(30).optional(),
        })
            .strict()
            .default({ keywords: [] }),
    })
        .strict(),
    knowledge: z
        .object({
        enabled: z.boolean(),
        sourceIds: z.array(z.string().uuid()).max(100).optional(),
    })
        .strict()
        .default({ enabled: true }),
    channels: z
        .object({
        whatsappAccountIds: z.array(z.string().uuid()).max(20).default([]),
        /** Confirmação explícita do risco (mesma do Typechat). */
        allowEvolution: z.boolean().optional(),
        startKeywords: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
        startOnAdLead: z.boolean().default(false),
        web: z.boolean().default(true),
    })
        .strict()
        .default({ whatsappAccountIds: [], startKeywords: [], startOnAdLead: false, web: true }),
    ai: z
        .object({
        provider: z.enum(["openai", "anthropic", "gemini"]).optional(),
        model: z.string().trim().max(80).optional(),
    })
        .strict()
        .default({}),
})
    .strict();
/** Marca da última montagem: é o que denuncia edição no editor avançado. */
export const agentBuildSchema = z
    .object({
    graphHash: z.string().max(64),
    builtAt: z.string().max(40),
})
    .strict();
export const flowSettingsSchema = z
    .object({
    /**
     * Resposta parcial vira lead: quem deu o telefone e parou no meio já entra
     * no CRM, marcado como "não concluiu". Ligado por padrão.
     */
    partialLead: z.boolean().optional(),
    /** Texto de consentimento mostrado antes do primeiro dado pessoal (LGPD). */
    consentText: z.string().max(600).optional(),
    /** Evento enviado à Meta quando o fluxo termina. Vazio = nenhum. */
    completeEvent: z.string().max(60).optional(),
    welcome: flowWelcomeSchema.optional(),
    whatsapp: z
        .object({
        /** Palavras que iniciam o fluxo quando a pessoa escreve. */
        startKeywords: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
        /**
         * Número não oficial só roda com confirmação explícita: robô em
         * Evolution é risco real de banimento do número da clínica.
         */
        allowEvolution: z.boolean().optional(),
        /** Teto de sessões novas por hora no WhatsApp. */
        maxNewSessionsPerHour: z.number().int().min(1).max(500).optional(),
        /** Depois de quanto tempo sem resposta a sessão é dada como abandonada. */
        abandonAfterMinutes: z.number().int().min(5).max(10080).optional(),
    })
        .strict()
        .optional(),
    /** Fluxo criado pela tela "Agente de IA": a configuração simples dele. */
    agent: agentConfigSchema.optional(),
    agentBuild: agentBuildSchema.optional(),
})
    .strict();
// ── CRUD (autenticado) ──────────────────────────────────────────────────────
/** Modelos de partida. "blank" é um fluxo com um bloco de texto só. */
export const FLOW_TEMPLATES = [
    "blank",
    "lead_form",
    "quiz_procedimento",
    "chat_boas_vindas",
    "chat_ia_triagem",
    "avaliacao_premium",
    "quiz_spin",
];
export const FLOW_TEMPLATE_LABEL = {
    blank: "Em branco",
    lead_form: "Formulário de captação (nome, WhatsApp, interesse)",
    quiz_procedimento: "Quiz: qual procedimento combina com você",
    chat_boas_vindas: "Typechat: boas-vindas e qualificação",
    chat_ia_triagem: "Typechat com IA: triagem e agendamento",
    avaliacao_premium: "Avaliação premium (estilo aplicação, com qualificação)",
    quiz_spin: "Quiz SPIN: descubra o que o seu rosto está pedindo",
};
export const createFlowSchema = z.object({
    kind: z.enum(FLOW_KINDS),
    name: z.string().trim().min(1).max(120),
    slug: flowSlugSchema.optional(),
    template: z.enum(FLOW_TEMPLATES).default("blank"),
});
export const updateFlowSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    slug: flowSlugSchema.optional(),
    draft: flowGraphSchema.optional(),
    theme: flowThemeSchema.optional(),
    settings: flowSettingsSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, "Nada para atualizar");
export const listFlowsQuerySchema = z.object({
    kind: z.enum(FLOW_KINDS).optional(),
    status: z.enum(FLOW_STATUSES).optional(),
});
export const listFlowSessionsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    status: z
        .enum(["active", "waiting_input", "handoff", "completed", "abandoned", "failed"])
        .optional(),
});
// ── Página pública (sem login) ──────────────────────────────────────────────
export const flowAttributionSchema = z
    .object({
    utm_source: z.string().max(200).optional(),
    utm_medium: z.string().max(200).optional(),
    utm_campaign: z.string().max(300).optional(),
    utm_content: z.string().max(300).optional(),
    utm_term: z.string().max(300).optional(),
    fbclid: z.string().max(500).optional(),
    fbc: z.string().max(500).optional(),
    fbp: z.string().max(200).optional(),
    gclid: z.string().max(500).optional(),
    landing: z.string().max(2048).optional(),
    referrer: z.string().max(2048).optional(),
})
    .strip();
export const startFlowSessionSchema = z.object({
    /** Cookie da página; liga visitas antes de a pessoa se identificar. */
    visitorId: z.string().max(80).optional(),
    attribution: flowAttributionSchema.default({}),
    /** `event_id` do PageView/Lead do pixel, para a CAPI não duplicar. */
    eventId: z.string().max(80).optional(),
});
export const flowInputSchema = z.object({
    /** Bloco que a página acha que está respondendo — protege contra clique duplo. */
    nodeId: z.string().min(1).max(64),
    /** Resposta da pergunta, ou o texto livre para o bloco de IA. */
    value: z.unknown(),
});
// ── Domínio próprio ─────────────────────────────────────────────────────────
/**
 * Nome de domínio aceito: rótulos de letras, números e hífen, separados por
 * ponto, terminando num rótulo que começa com letra (o TLD). É a MESMA regra
 * que o `docker/sync-flow-domains.sh` confere antes de escrever o YAML do
 * Traefik — o que passa aqui é o que pode ir para lá.
 */
export const FLOW_DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/;
/**
 * Limpa o que a pessoa colou: protocolo, caminho, porta, ponto final e
 * maiúsculas. "https://Quiz.Clinica.com.br/abc" → "quiz.clinica.com.br".
 */
export function normalizeFlowDomainInput(bruto) {
    return bruto
        .trim()
        .toLowerCase()
        .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
        .replace(/[/?#].*$/, "")
        .replace(/:\d*$/, "")
        .replace(/\.$/, "");
}
export const flowDomainSchema = z
    .string()
    .max(300)
    .transform(normalizeFlowDomainInput)
    .pipe(z
    .string()
    .min(4, "Informe o domínio, ex.: quiz.suaclinica.com.br")
    .max(253, "Domínio longo demais")
    .regex(FLOW_DOMAIN_PATTERN, "Domínio inválido. Use algo como quiz.suaclinica.com.br"));
export const createFlowDomainSchema = z.object({
    domain: flowDomainSchema,
    defaultFlowId: z.string().uuid().nullable().optional(),
});
export const updateFlowDomainSchema = z.object({
    defaultFlowId: z.string().uuid().nullable(),
});
export const resolveFlowDomainQuerySchema = z.object({
    host: z.string().max(300).optional(),
});
export const FLOW_DOMAIN_STATUSES = ["pending", "verified", "failed"];
export const FLOW_DOMAIN_STATUS_LABEL = {
    pending: "Aguardando DNS",
    verified: "No ar",
    failed: "DNS não confere",
};
// ── Agente de IA: API ───────────────────────────────────────────────────────
export const createAgentSchema = z.object({ config: agentConfigSchema });
export const updateAgentSchema = z.object({ config: agentConfigSchema });
/**
 * Um turno de teste no painel "Conversar com o agente". A tela guarda a
 * conversa e o que já foi coletado; o servidor não grava sessão nenhuma.
 */
export const agentTestSchema = z.object({
    messages: z
        .array(z.object({
        role: z.enum(["user", "assistant"]),
        text: z.string().trim().min(1).max(2000),
    }))
        .min(1)
        .max(60),
    collected: z.record(z.string().max(60), z.string().max(1000)).optional(),
});
//# sourceMappingURL=flows.js.map